// ---------------------------------------------------------------------------
// Caster Room — shared types and constants for WS room infrastructure
// ---------------------------------------------------------------------------
import { z } from 'zod';
/**
 * Default health configurations keyed by game ID.
 * Use `satisfies` to validate shape without widening per-game literal types.
 *
 * - digimon: Security stack, 5 cards, no defaultValue (starts at maxValue)
 * - gundam: Security stack, maxValue 99 enables numeric fallback above 10 pips, defaultValue 6
 * - chrono-core: Two-stage life bar (30 → 12), second stage rendered in rose
 */
export const DEFAULT_HEALTH_CONFIGS = {
    digimon: {
        label: 'Security',
        displayMode: 'security',
        maxValue: 50,
        defaultValue: 5,
        iconPath: '/digimon-security-icon.png',
    },
    gundam: {
        label: 'Shields',
        displayMode: 'security',
        maxValue: 50,
        defaultValue: 6,
        iconPath: '/gundam-shields-icon.png',
    },
    'chrono-core': {
        label: 'Life',
        displayMode: 'bar',
        maxValue: 30,
        secondStageMax: 12,
        secondStageColor: '#FF3FAF',
    },
    'grand-archive': {
        label: 'Life',
        displayMode: 'bar',
        maxValue: 15,
        adjustableMax: true,
    },
};
/**
 * Single source of truth for PlayerProfile defaults.
 *
 * Every consumer that needs a "blank" PlayerProfile (the web caster store's
 * initial/reset state, the API's snapshot coercion for absent players) MUST
 * call this factory instead of hand-spelling the field list. Adding a new
 * optional field to PlayerProfile only requires updating this function —
 * no other file needs to re-spell the default literal.
 *
 * Returns a fresh object on every call (no shared references) so callers can
 * safely mutate/spread the result.
 */
export function makeDefaultPlayerProfile() {
    return {
        name: '',
        archetype: '',
        pilotCardUrl: null,
        coreCardUrl: null,
        weaponIdentity: null,
        equipment: null,
        pilotLife: null,
        coreShield: null,
        pilotName: null,
        coreName: null,
        gaIdentitySpiritUrl: null,
        gaIdentityChampionUrl: null,
        featuredCardImageUrl: null,
    };
}
// ---------------------------------------------------------------------------
// Delta-to-overlay event mapping
// ---------------------------------------------------------------------------
/**
 * Maps caster store delta fields to named overlay events for overlay widgets.
 * Fields in ALLOWED_DELTA_FIELDS that are NOT in this map are WS-only
 * (exchanged between producer/casters but not bridged to overlay clients):
 * activeTableId, broadcastTableId, matchNotes, liveKitRoomName, liveKitInviteCode, gameCounters.
 */
export const DELTA_OVERLAY_EVENT_MAP = {
    cardBackImageUrl: 'roomMeta',
    spotlightImageUrl: 'card', // field uses "spotlight" naming, SSE event is "card" for brevity
    p1: 'playerWidget',
    p2: 'playerWidget',
    p1Score: 'playerWidget',
    p2Score: 'playerWidget',
    bestOf: 'playerWidget',
    turnIndicator: 'playerWidget',
    p1Health: 'playerWidget',
    p2Health: 'playerWidget',
    p1HealthStage: 'playerWidget',
    p2HealthStage: 'playerWidget',
    healthConfig: 'playerWidget',
    p1Record: 'playerWidget',
    p2Record: 'playerWidget',
    showHealth: 'playerWidget',
    showPhotos: 'playerWidget',
    showScores: 'playerWidget',
    avatarShape: 'playerWidget',
    p1Photo: 'playerPhoto',
    p2Photo: 'playerPhoto',
    animationSettings: 'animationSettings',
    timer: 'timer',
    videoTransforms: 'videoTransforms',
    overlayLayout: 'overlayLayout',
    pipPosition: 'pipPosition',
    showCameraFrames: 'showCameraFrames',
    cameraBorderColor: 'cameraBorderColor',
    sidePlayerAlign: 'playerWidget',
    playerWidgetAlign: 'playerWidget',
    sidePlayerPanelSize: 'playerWidget',
    p1Identity: 'playerWidget',
    p2Identity: 'playerWidget',
    healthConfigs: 'playerWidget',
    p1Healths: 'playerWidget',
    p2Healths: 'playerWidget',
    p1HealthStages: 'playerWidget',
    p2HealthStages: 'playerWidget',
    p1HealthMaxes: 'playerWidget',
    p2HealthMaxes: 'playerWidget',
    deckComparisonPayload: 'comparison',
    deckListPayload: 'deckList',
};
/** Allowlist of valid overlay event names (prevents newline injection) */
export const VALID_OVERLAY_EVENTS = new Set([
    'card',
    'playerWidget',
    'playerPhoto',
    'animationSettings',
    'timer',
    'roomMeta',
    'videoTransforms',
    'overlayLayout',
    'pipPosition',
    'showCameraFrames',
    'cameraBorderColor',
    'comparison',
    'deckList',
]);
// ---------------------------------------------------------------------------
// Delta field allowlist
// ---------------------------------------------------------------------------
/** Allowlist of delta fields accepted from producers. Rejects unknown/dangerous keys. */
export const ALLOWED_DELTA_FIELDS = new Set([
    'cardBackImageUrl',
    'spotlightImageUrl',
    'p1',
    'p2',
    'p1Score',
    'p2Score',
    'bestOf',
    'timer',
    'gameCounters',
    'activeTableId',
    'matchNotes',
    'turnIndicator',
    'liveKitRoomName',
    'liveKitInviteCode',
    'videoTransforms',
    'overlayLayout',
    'pipPosition',
    'showCameraFrames',
    'cameraBorderColor',
    'p1Health',
    'p2Health',
    'p1HealthStage',
    'p2HealthStage',
    'healthConfig',
    'p1Photo',
    'p2Photo',
    'p1Record',
    'p2Record',
    'animationSettings',
    'showHealth',
    'showPhotos',
    'showScores',
    'avatarShape',
    'sidePlayerAlign',
    'playerWidgetAlign',
    'sidePlayerPanelSize',
    'p1Identity',
    'p2Identity',
    'healthConfigs',
    'p1Healths',
    'p2Healths',
    'p1HealthStages',
    'p2HealthStages',
    'p1HealthMaxes',
    'p2HealthMaxes',
    'tablesById',
    'tableOrder',
    'broadcastTableId',
    'deckComparisonPayload',
    'deckListPayload',
]);
// ---------------------------------------------------------------------------
// Table field validation constants
// ---------------------------------------------------------------------------
/**
 * Fields within a MatchTable entry that are forbidden from wire transit.
 * These are large, local-only fields that must never be sent in a delta.
 * Validation REJECTS (not strips) payloads containing these — the producer
 * should never send them.
 */
export const FORBIDDEN_TABLE_FIELDS = new Set([
    'p1Deck',
    'p2Deck',
    'playLog',
]);
/**
 * The subset of MatchTable fields that are safe to sync over the wire.
 * Any field NOT in this set (and not in FORBIDDEN_TABLE_FIELDS) is silently
 * stripped during validation.
 */
export const SYNC_TABLE_FIELDS = new Set([
    'id',
    'label',
    'p1',
    'p2',
    'p1Score',
    'p2Score',
    'bestOf',
    'gameCounters',
    'turnIndicator',
    'p1Health',
    'p2Health',
    'p1HealthStage',
    'p2HealthStage',
    'healthConfig',
    'p1Photo',
    'p2Photo',
    'p1Record',
    'p2Record',
    'animationSettings',
    'showHealth',
    'showPhotos',
    'showScores',
    'avatarShape',
    'timer',
    'matchNotes',
    'matchVideoUrl',
    'healthConfigs',
    'p1Healths',
    'p2Healths',
    'p1HealthStages',
    'p2HealthStages',
    'p1HealthMaxes',
    'p2HealthMaxes',
]);
// ===========================================================================
// D123a — Boundary validation schemas (monitor-mode, colocated with types)
// ===========================================================================
//
// These schemas validate inbound caster/overlay WS payloads at browser trust
// boundaries (web `useCasterWebSocket.ts`, overlay `useWsEvents.ts`) and at the
// api caster-relay ingress. In D123a they run in MONITOR MODE ONLY: `safeParse`
// for signal, report a mismatch, then forward the ORIGINAL object unchanged.
// The parsed clone is NEVER forwarded (Zod v4 `z.object()` strips unknown keys,
// so forwarding it would drop fields newer deployed producers send — breaking
// forward/backward compat with stale OBS builds and Firestore-hydrated snapshot
// replays).
//
// Authoring conventions (spec §4, ADR-14 mirror-with-drift-guard):
//   - `*_FIELDS` const list + hand-written interface + `z.object` mirroring the
//     list; a drift-guard unit test asserts `schema.shape` keys === the list.
//   - EVERY schema here is LOOSE — never `.strict()`. z.object already accepts
//     (and strips-on-parse) unknown keys; we never `.strict()`.
//   - `Timestamp`-typed fields → `z.unknown()` (keep firebase out of lib).
//   - Zero `any`. Side-effect-free module.
//
// Hot/structural tiering (spec §2, binding): a single ROOM_DELTA carries both
// HOT fields (high-frequency: scores, health scalars + array-health variants,
// turnIndicator, spotlightImageUrl, videoTransforms) and STRUCTURAL fields
// (PlayerProfile, comparison/deckList payloads, healthConfig(s), animation,
// camera/layout config, identities/photos/records, bestOf, timer). Only
// structural fields get a per-field payload schema; hot fields stay
// envelope-only (validated only as part of the envelope, never deep-schema'd on
// the hot path). See `DELTA_STRUCTURAL_FIELD_SCHEMAS` below — the default rule is
// "any ALLOWED_DELTA_FIELDS member not present there is envelope-only (hot)".
// ---------------------------------------------------------------------------
// --- Reusable value schemas (shared by delta + overlay tiers) --------------
/** `videoTransform.types.ts` OverlayLayout — 'side-by-side' | 'pip' | 'top-bottom'. */
export const OverlayLayoutSchema = z.enum(['side-by-side', 'pip', 'top-bottom']);
/** `videoTransform.types.ts` PipPosition. */
export const PipPositionSchema = z.enum([
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
]);
/** `videoTransform.types.ts` CameraBorderColor — discriminated on `mode`. */
export const CameraBorderColorSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('brand') }),
    z.object({ mode: z.literal('solid'), color: z.string() }),
    z.object({
        mode: z.literal('gradient'),
        stops: z.union([
            z.tuple([z.string(), z.string()]),
            z.tuple([z.string(), z.string(), z.string()]),
        ]),
    }),
]);
/**
 * Timer WIRE shape (low-frequency STRUCTURAL). This is the shape a timer is
 * transformed INTO on emit (`useCasterWebSocket.ts` ~:576–580 and ~:321–324):
 * `{ startedAt, duration, isRunning }` — NOT the stored `MatchTable.timer`
 * shape (`{ duration, remaining, isRunning }`). `startedAt` is null when the
 * timer is not running.
 */
export const TIMER_WIRE_FIELDS = ['startedAt', 'duration', 'isRunning'];
export const TimerWireSchema = z.object({
    startedAt: z.number().nullable(),
    duration: z.number(),
    isRunning: z.boolean(),
});
// --- PlayerProfile ----------------------------------------------------------
export const PLAYER_PROFILE_FIELDS = [
    'name',
    'record',
    'headshotUrl',
    'archetype',
    'pilotCardUrl',
    'coreCardUrl',
    'weaponIdentity',
    'equipment',
    'pilotLife',
    'coreShield',
    'pilotName',
    'coreName',
    'gaIdentitySpiritUrl',
    'gaIdentityChampionUrl',
    'featuredCardImageUrl',
];
export const PlayerProfileSchema = z.object({
    name: z.string(),
    record: z.string().optional(),
    headshotUrl: z.string().optional(),
    archetype: z.string(),
    pilotCardUrl: z.string().nullable().optional(),
    coreCardUrl: z.string().nullable().optional(),
    weaponIdentity: z
        .array(z.object({ name: z.string(), weaponType: z.string().nullable() }))
        .nullable()
        .optional(),
    equipment: z
        .array(z.object({ name: z.string(), subtype: z.string().nullable() }))
        .nullable()
        .optional(),
    pilotLife: z.number().nullable().optional(),
    coreShield: z.number().nullable().optional(),
    pilotName: z.string().nullable().optional(),
    coreName: z.string().nullable().optional(),
    gaIdentitySpiritUrl: z.string().nullable().optional(),
    gaIdentityChampionUrl: z.string().nullable().optional(),
    featuredCardImageUrl: z.string().nullable().optional(),
});
// --- HealthConfig -----------------------------------------------------------
export const HEALTH_CONFIG_FIELDS = [
    'label',
    'displayMode',
    'maxValue',
    'defaultValue',
    'secondStageMax',
    'secondStageColor',
    'iconPath',
    'color',
    'adjustableMax',
];
export const HealthConfigSchema = z.object({
    label: z.string(),
    displayMode: z.enum(['pips', 'bar', 'security']),
    maxValue: z.number(),
    defaultValue: z.number().optional(),
    secondStageMax: z.number().optional(),
    secondStageColor: z.string().optional(),
    iconPath: z.string().optional(),
    color: z.string().optional(),
    adjustableMax: z.boolean().optional(),
});
// --- AnimationSettings ------------------------------------------------------
export const ANIMATION_SETTINGS_FIELDS = [
    'mode',
    'preset',
    'cardEntry',
    'cardExit',
    'playerEntry',
    'playerUpdate',
    'timerEntry',
];
export const AnimationSettingsSchema = z.object({
    mode: z.enum(['preset', 'custom']),
    preset: z.enum(['clean', 'dynamic', 'dramatic', 'retro', 'classic']).optional(),
    cardEntry: z.string().optional(),
    cardExit: z.string().optional(),
    playerEntry: z.string().optional(),
    playerUpdate: z.string().optional(),
    timerEntry: z.string().optional(),
});
// --- ComparisonDeckStats + ComparisonPayload --------------------------------
//
// ComparisonDeckStats travels off the hot path (structural). Top-level field
// CONTAINERS (array / object / primitive / nullability) are typed accurately —
// that is the primary drift signal (a stat field going missing or changing
// container type, the profile-shape-change-amplification bug class). Deeply
// nested chart leaves use loose element schemas to avoid monitor-mode false
// positives from legitimate additive leaf variation.
const labeledCount = z.object({
    label: z.string(),
    count: z.number(),
    color: z.string().optional(),
});
export const COMPARISON_DECK_STATS_FIELDS = [
    'totalCards',
    'zoneFill',
    'colorDistribution',
    'hasMultiColorCards',
    'typeDistribution',
    'showTypePercentages',
    'costCurve',
    'costCurveLabel',
    'primaryAxisChartType',
    'combinedCostCurve',
    'combinedCostCurveLabel',
    'secondaryCurve',
    'secondaryCurveLabel',
    'secondaryAxisChartType',
    'additionalCurves',
    'subtypeDistributions',
    'subtypeDistribution',
    'subtypeLabel',
    'showSubtypePercentages',
    'speedDistribution',
    'speedLabel',
    'keywordDistribution',
    'keywordLabel',
    'binaryDistribution',
    'binaryDistributionLabel',
    'binaryDistributionColors',
    'specialMechanicCounts',
    'typeDistributionByZone',
    'subtypeDistributionsByZone',
    'gaElementDistribution',
    'gaLifeCurve',
    'gaStartingLoadout',
];
export const ComparisonDeckStatsSchema = z.object({
    totalCards: z.number(),
    zoneFill: z.array(z.object({
        zoneId: z.string(),
        label: z.string(),
        current: z.number(),
        min: z.number(),
        max: z.number(),
    })),
    colorDistribution: z.array(labeledCount),
    hasMultiColorCards: z.boolean(),
    typeDistribution: z.array(labeledCount),
    showTypePercentages: z.boolean(),
    costCurve: z.array(labeledCount),
    costCurveLabel: z.string(),
    primaryAxisChartType: z.enum(['bar', 'lollipop', 'horizontal']),
    combinedCostCurve: z.array(z.unknown()).nullable(),
    combinedCostCurveLabel: z.string().nullable(),
    secondaryCurve: z.array(labeledCount).nullable(),
    secondaryCurveLabel: z.string().nullable(),
    secondaryAxisChartType: z.enum(['bar', 'horizontal']),
    additionalCurves: z.array(z.unknown()),
    subtypeDistributions: z.array(z.unknown()).nullable(),
    subtypeDistribution: z.array(labeledCount).nullable(),
    subtypeLabel: z.string().nullable(),
    showSubtypePercentages: z.boolean(),
    speedDistribution: z.array(labeledCount).nullable(),
    speedLabel: z.string().nullable(),
    keywordDistribution: z.array(labeledCount).nullable(),
    keywordLabel: z.string().nullable(),
    binaryDistribution: z.array(labeledCount).nullable(),
    binaryDistributionLabel: z.string().nullable(),
    binaryDistributionColors: z.tuple([z.string(), z.string()]).nullable(),
    specialMechanicCounts: z.array(labeledCount).nullable(),
    typeDistributionByZone: z.record(z.string(), z.array(labeledCount)).optional(),
    subtypeDistributionsByZone: z.record(z.string(), z.array(labeledCount)).optional(),
    gaElementDistribution: z.array(labeledCount).optional(),
    gaLifeCurve: z.array(z.object({ label: z.string(), life: z.number() })).optional(),
    gaStartingLoadout: z.record(z.string(), z.unknown()).optional(),
});
export const COMPARISON_PAYLOAD_FIELDS = ['p1', 'p2', 'colorLabel', 'gameId'];
export const ComparisonPayloadSchema = z.object({
    p1: ComparisonDeckStatsSchema.nullable(),
    p2: ComparisonDeckStatsSchema.nullable(),
    colorLabel: z.string(),
    gameId: z.string(),
});
// --- DeckList overlay payload -----------------------------------------------
export const DECK_CARD_REF_FIELDS = ['name', 'quantity', 'imageUrl'];
export const DeckCardRefSchema = z.object({
    name: z.string(),
    quantity: z.number(),
    imageUrl: z.string().nullable(),
});
export const DECK_ZONE_GROUP_FIELDS = ['zoneId', 'label', 'cards'];
export const DeckZoneGroupSchema = z.object({
    zoneId: z.string(),
    label: z.string(),
    cards: z.array(DeckCardRefSchema),
});
export const DECK_PROFILE_LAYOUT_SETTINGS_FIELDS = [
    'layout',
    'mainDeckColumns',
    'sideboardColumns',
];
export const DeckProfileLayoutSettingsSchema = z.object({
    layout: z.enum(['grid', 'side-by-side']),
    mainDeckColumns: z.number(),
    sideboardColumns: z.number(),
});
export const DECK_LIST_PAYLOAD_FIELDS = [
    'p1',
    'p2',
    'gameId',
    'p1Layout',
    'p2Layout',
];
export const DeckListPayloadSchema = z.object({
    p1: z.array(DeckZoneGroupSchema).nullable(),
    p2: z.array(DeckZoneGroupSchema).nullable(),
    gameId: z.string(),
    p1Layout: DeckProfileLayoutSettingsSchema.optional(),
    p2Layout: DeckProfileLayoutSettingsSchema.optional(),
});
// --- ROOM_STATE: two schemas (wrapper vs nested snapshot) -------------------
/**
 * ROOM_STATE envelope `data` — the WRAPPER `{ state, producerConnected }`
 * (`RoomStateData`). `state` is the hydrated snapshot record; its shape is
 * validated separately by `RoomSnapshotSchema` (below), because the wrapper and
 * the snapshot are two distinct trust layers (spec: reconnect/hydrated-shape
 * hole vs the overlay's FULL_STATE coverage).
 */
export const ROOM_STATE_DATA_FIELDS = ['state', 'producerConnected'];
export const RoomStateDataSchema = z.object({
    state: z.record(z.string(), z.unknown()),
    producerConnected: z.boolean(),
});
export const ROOM_DELTA_DATA_FIELDS = ['delta', 'tableId'];
export const RoomDeltaDataSchema = z.object({
    delta: z.record(z.string(), z.unknown()),
    tableId: z.string(),
});
export const PLAYER_WIDGET_PAYLOAD_FIELDS = [
    'p1',
    'p2',
    'p1Score',
    'p2Score',
    'bestOf',
    'turnIndicator',
    'p1Health',
    'p2Health',
    'p1HealthStage',
    'p2HealthStage',
    'healthConfig',
    'p1Record',
    'p2Record',
    'showHealth',
    'showPhotos',
    'showScores',
    'avatarShape',
    'sidePlayerAlign',
    'playerWidgetAlign',
    'sidePlayerPanelSize',
    'p1Identity',
    'p2Identity',
    'healthConfigs',
    'p1Healths',
    'p2Healths',
    'p1HealthMaxes',
    'p2HealthMaxes',
    'p1HealthStages',
    'p2HealthStages',
];
export const PlayerWidgetPayloadSchema = z.object({
    p1: PlayerProfileSchema,
    p2: PlayerProfileSchema,
    // HOT — envelope-only, loosened (re-emits per score/health click).
    p1Score: z.unknown(),
    p2Score: z.unknown(),
    bestOf: z.number(),
    turnIndicator: z.unknown(),
    p1Health: z.unknown(),
    p2Health: z.unknown(),
    p1HealthStage: z.unknown(),
    p2HealthStage: z.unknown(),
    // STRUCTURAL config objects.
    healthConfig: HealthConfigSchema.nullable(),
    p1Record: z.string(),
    p2Record: z.string(),
    showHealth: z.boolean(),
    showPhotos: z.boolean(),
    showScores: z.boolean(),
    avatarShape: z.string(),
    sidePlayerAlign: z.string(),
    playerWidgetAlign: z.string(),
    sidePlayerPanelSize: z.string(),
    p1Identity: z.string().nullable(),
    p2Identity: z.string().nullable(),
    healthConfigs: z.array(HealthConfigSchema).nullable(),
    // HOT — array-health variants, loosened.
    p1Healths: z.unknown(),
    p2Healths: z.unknown(),
    p1HealthMaxes: z.unknown(),
    p2HealthMaxes: z.unknown(),
    p1HealthStages: z.unknown(),
    p2HealthStages: z.unknown(),
});
// --- ROOM_DELTA per-field hot/structural tier map ---------------------------
//
// STRUCTURAL delta fields → their per-field payload schema. Any
// `ALLOWED_DELTA_FIELDS` member NOT present here is envelope-only (HOT) and gets
// NO per-field schema on the hot path (spec §2 default rule). `videoTransforms`
// (hottest), scores, health scalars/variants, turnIndicator, spotlightImageUrl,
// gameCounters, matchNotes, cardBackImageUrl, and the WS-only control fields
// (activeTableId/broadcastTableId/liveKit*) are all HOT by omission. The
// display toggles (showHealth/showPhotos/showScores/avatarShape/align/panelSize)
// are hot-by-default too — spec §2 does not name them structural.
export const DELTA_STRUCTURAL_FIELD_SCHEMAS = {
    p1: PlayerProfileSchema,
    p2: PlayerProfileSchema,
    bestOf: z.number(),
    timer: TimerWireSchema,
    overlayLayout: OverlayLayoutSchema,
    pipPosition: PipPositionSchema,
    showCameraFrames: z.boolean(),
    cameraBorderColor: CameraBorderColorSchema,
    p1Photo: z.string().nullable(),
    p2Photo: z.string().nullable(),
    p1Record: z.string(),
    p2Record: z.string(),
    p1Identity: z.string().nullable(),
    p2Identity: z.string().nullable(),
    animationSettings: AnimationSettingsSchema,
    healthConfig: HealthConfigSchema,
    healthConfigs: z.array(HealthConfigSchema),
    deckComparisonPayload: ComparisonPayloadSchema,
    deckListPayload: DeckListPayloadSchema,
    tablesById: z.record(z.string(), z.record(z.string(), z.unknown())),
    tableOrder: z.array(z.string()),
};
/** Structural delta field names (keys of `DELTA_STRUCTURAL_FIELD_SCHEMAS`). */
export const DELTA_STRUCTURAL_FIELDS = new Set(Object.keys(DELTA_STRUCTURAL_FIELD_SCHEMAS));
/** HOT (envelope-only) delta fields — every ALLOWED_DELTA_FIELDS member that is
 * NOT structural. Derived so the two tiers are exhaustive and mutually exclusive
 * over ALLOWED_DELTA_FIELDS by construction (asserted in the drift-guard test). */
export const DELTA_HOT_FIELDS = new Set([...ALLOWED_DELTA_FIELDS].filter((f) => !DELTA_STRUCTURAL_FIELDS.has(f)));
// --- Overlay schema families (two, per spec §2) -----------------------------
//
// Family 1 — FRAME shape: a plain `z.union` distinguishing the FULL_STATE batch
// (`{ type:'FULL_STATE', events:[…] }`) from a single `{ eventName, data }`
// frame (`useWsEvents.ts` ~:90). Per-event `data` is left loose here; validating
// each event's data is Family 2's job.
// Family 2 — EVENT shape: a `z.discriminatedUnion` on `eventName` across the 13
// `VALID_OVERLAY_EVENTS`. HOT events (`card`/spotlight, `videoTransforms`) carry
// `z.unknown()` data (envelope-only); config-frequency events (overlayLayout,
// pipPosition, showCameraFrames, cameraBorderColor) and the structural payloads
// (playerWidget/comparison/deckList/playerPhoto/animationSettings/timer/roomMeta)
// carry full schemas.
const overlayGenericEvent = z.object({
    eventName: z.string(),
    data: z.record(z.string(), z.unknown()),
});
export const OverlayFullStateSchema = z.object({
    type: z.literal('FULL_STATE'),
    events: z.array(overlayGenericEvent),
});
export const OverlaySingleFrameSchema = overlayGenericEvent;
/** Family 1 — overlay frame envelope (FULL_STATE batch vs single frame). */
export const OverlayFrameSchema = z.union([
    OverlayFullStateSchema,
    OverlaySingleFrameSchema,
]);
/** Family 2 — per-event discriminated union on `eventName` (13 events). */
export const OverlayEventSchema = z.discriminatedUnion('eventName', [
    // HOT — loose data.
    z.object({ eventName: z.literal('card'), data: z.unknown() }),
    z.object({ eventName: z.literal('videoTransforms'), data: z.unknown() }),
    // STRUCTURAL payloads.
    z.object({ eventName: z.literal('playerWidget'), data: PlayerWidgetPayloadSchema }),
    z.object({
        eventName: z.literal('playerPhoto'),
        data: z.object({
            p1Photo: z.string().nullable().optional(),
            p2Photo: z.string().nullable().optional(),
        }),
    }),
    z.object({ eventName: z.literal('animationSettings'), data: AnimationSettingsSchema }),
    z.object({ eventName: z.literal('timer'), data: TimerWireSchema }),
    z.object({
        eventName: z.literal('roomMeta'),
        data: z.object({
            gameId: z.string().optional(),
            cardBackImageUrl: z.string().optional(),
        }),
    }),
    // STRUCTURAL config-frequency events.
    z.object({
        eventName: z.literal('overlayLayout'),
        data: z.object({ overlayLayout: OverlayLayoutSchema }),
    }),
    z.object({
        eventName: z.literal('pipPosition'),
        data: z.object({ pipPosition: PipPositionSchema }),
    }),
    z.object({
        eventName: z.literal('showCameraFrames'),
        data: z.object({ showCameraFrames: z.boolean() }),
    }),
    z.object({
        eventName: z.literal('cameraBorderColor'),
        data: z.object({ cameraBorderColor: CameraBorderColorSchema }),
    }),
    z.object({ eventName: z.literal('comparison'), data: ComparisonPayloadSchema }),
    z.object({ eventName: z.literal('deckList'), data: DeckListPayloadSchema }),
]);
/** eventName → structural data schema, for the overlay per-field/per-event tier
 * split at ingress. HOT events (`card`, `videoTransforms`) are intentionally
 * ABSENT — they are envelope-only and never deep-validated. */
export const OVERLAY_STRUCTURAL_EVENT_SCHEMAS = {
    playerWidget: PlayerWidgetPayloadSchema,
    playerPhoto: z.object({
        p1Photo: z.string().nullable().optional(),
        p2Photo: z.string().nullable().optional(),
    }),
    animationSettings: AnimationSettingsSchema,
    timer: TimerWireSchema,
    roomMeta: z.object({
        gameId: z.string().optional(),
        cardBackImageUrl: z.string().optional(),
    }),
    overlayLayout: z.object({ overlayLayout: OverlayLayoutSchema }),
    pipPosition: z.object({ pipPosition: PipPositionSchema }),
    showCameraFrames: z.object({ showCameraFrames: z.boolean() }),
    cameraBorderColor: z.object({ cameraBorderColor: CameraBorderColorSchema }),
    comparison: ComparisonPayloadSchema,
    deckList: DeckListPayloadSchema,
};
/** Overlay HOT event names — genuinely high-frequency, envelope-only (no data
 * schema). Every `VALID_OVERLAY_EVENTS` member not in
 * `OVERLAY_STRUCTURAL_EVENT_SCHEMAS`. */
export const OVERLAY_HOT_EVENTS = new Set(['card', 'videoTransforms']);
// --- Nested room snapshot (loose, all-optional) -----------------------------
//
// The hydrated `state` record inside ROOM_STATE. LOOSE and ALL-OPTIONAL: it may
// legitimately replay OLDER Firestore-hydrated shapes and stale-build snapshots,
// so it must never make a previously-valid payload invalid. Structural fields
// reuse their per-field schemas (`.optional()`); hot fields are `z.unknown()`.
// Unknown keys (non-delta snapshot fields like `matchVideoUrl`, `gameId`) pass
// through — z.object is loose and we forward the original regardless.
//
// Built from `ALLOWED_DELTA_FIELDS` + `DELTA_STRUCTURAL_FIELD_SCHEMAS` so it can
// never drift from the delta tier map. It has NO `*_FIELDS` drift guard — it is a
// synthetic superset, not a mirror of a single hand-written interface.
const roomSnapshotShape = {};
for (const field of ALLOWED_DELTA_FIELDS) {
    const structural = DELTA_STRUCTURAL_FIELD_SCHEMAS[field];
    roomSnapshotShape[field] = (structural ?? z.unknown()).optional();
}
export const RoomSnapshotSchema = z.object(roomSnapshotShape);
// --- Topdeck attach (ROOM_TOPDECK_ATTACH, bidirectional) --------------------
export const TOPDECK_ATTACH_REQUEST_FIELDS = [
    'tournamentId',
    'round',
    'table',
];
export const TopdeckAttachRequestSchema = z.object({
    tournamentId: z.string(),
    round: z.number(),
    table: z.number(),
});
export const TOPDECK_ATTACH_PLAYER_FIELDS = ['slot', 'name'];
export const TopdeckAttachPlayerSchema = z.object({
    slot: z.string(),
    name: z.string(),
});
export const TOPDECK_ATTACH_SUCCESS_FIELDS = [
    'ok',
    'tournamentId',
    'round',
    'table',
    'gameId',
    'pairing',
    'decklists',
    'roomUrl',
];
export const TopdeckAttachSuccessSchema = z.object({
    ok: z.literal(true),
    tournamentId: z.string(),
    round: z.number(),
    table: z.number(),
    gameId: z.string(),
    pairing: z.object({ players: z.array(TopdeckAttachPlayerSchema) }),
    // RawTopdeckSlot is a large imported type; kept opaque here (structural
    // presence check only) to avoid re-modeling the topdeck decklist shape.
    decklists: z.record(z.string(), z.unknown()),
    roomUrl: z.string(),
});
export const TOPDECK_ATTACH_FAILURE_FIELDS = [
    'ok',
    'tournamentId',
    'round',
    'table',
    'error',
    'code',
];
export const TopdeckAttachFailureSchema = z.object({
    ok: z.literal(false),
    tournamentId: z.string(),
    round: z.number(),
    table: z.number(),
    error: z.string(),
    code: z.enum(['NOT_OWNER', 'NOT_PRODUCER', 'MAPPING_NOT_FOUND', 'BYE']),
});
export const TopdeckAttachResponseSchema = z.discriminatedUnion('ok', [
    TopdeckAttachSuccessSchema,
    TopdeckAttachFailureSchema,
]);
//# sourceMappingURL=casterRoom.types.js.map